import { useState, useMemo, useCallback, useEffect } from 'react';
import { getIn } from 'formik';

export interface TableColumn {
  header: string;
  field: string;
  fieldType: string;
  width?: number;
  options?: {
    [key: string]: any;
  };
}

export interface RowData {
  [key: string]: any;
}

export interface UseInternalTableProps {
  name: string;
  columns: TableColumn[];
  initialValues: any;
  setFieldValue: (field: string, value: any) => void;
}

const useInternalTable = ({ name, columns, initialValues, setFieldValue }: UseInternalTableProps) => {
  // Initialize an empty row based on column structure
  const emptyRow = useMemo<RowData>(() => {
    const newRow: RowData = {};
    columns.forEach(column => {
      newRow[column.field] = '';
    });
    return newRow;
  }, [columns]);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  // Local state to manage table rows
  const [localRows, setLocalRows] = useState<RowData[]>([]);

  const fieldInitialValue = getIn(initialValues, name);

  // Initialize local rows from formik or initial values
  useEffect(() => {
    // If formik value is not an array, try to parse the initial value
    if (typeof fieldInitialValue === 'string' && fieldInitialValue) {
      const isValid = isValidJSON(fieldInitialValue);
      const parsedValue = JSON.parse(isValid ? fieldInitialValue : '[]');
      if (Array.isArray(parsedValue) && parsedValue.length > 0) {
        setLocalRows(parsedValue);
        return;
      }
    }

    setLocalRows([]);
  }, [fieldInitialValue]);

  // Sort rows by first column
  const sortRows = useCallback(
    (values: RowData[]) => {
      // First, filter out rows with any empty cells
      const filteredValues = values.filter(row => {
        // Check that every column has a non-empty value
        return columns.every(column => {
          const value = row[column.field];
          return value !== undefined && value !== null && value !== '';
        });
      });

      if (columns.length > 0) {
        const firstColumnField = columns[0].field;
        return [...filteredValues].sort((a, b) => {
          const valueA = String(a[firstColumnField] ?? '').toLowerCase();
          const valueB = String(b[firstColumnField] ?? '').toLowerCase();
          return valueA.localeCompare(valueB);
        });
      }
      return filteredValues;
    },
    [columns],
  );

  // Sync local rows with formik state on form submission
  const syncToFormik = useCallback(
    (localRows: RowData[]) => {
      const sortedRows = sortRows(localRows);
      const initial = isValidJSON(fieldInitialValue) ? fieldInitialValue : '{}';
      const initialValue = JSON.parse(initial);
      if (JSON.stringify(initialValue) === JSON.stringify(sortedRows)) {
        return;
      }
      setFieldValue(name, sortedRows);
    },
    [sortRows, setFieldValue, name, fieldInitialValue],
  );

  // Handler for input changes in any row
  const handleFieldChange = useCallback((rowIndex: number, fieldName: string, fieldValue: any) => {
    setLocalRows(prevRows => {
      const updatedRows = [...prevRows];
      if (updatedRows[rowIndex]) {
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          [fieldName]: fieldValue,
        };
      }
      return updatedRows;
    });
  }, []);

  // Add a new empty row
  const addEmptyRow = useCallback(() => {
    const result = [...localRows, { ...emptyRow }];
    setLocalRows(result);
    syncToFormik(result);
  }, [emptyRow, localRows, syncToFormik]);

  // Handler for deleting a row
  const handleDeleteRow = useCallback(
    (indexToDelete: number) => {
      const result = localRows.filter((_, index) => index !== indexToDelete);
      syncToFormik(result);
      setLocalRows(result);
    },
    [localRows, syncToFormik],
  );

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  return {
    localRows,
    isCollapsed,
    emptyRow,
    handleFieldChange,
    addEmptyRow,
    handleDeleteRow,
    toggleCollapse,
    syncToFormik,
  };
};

export default useInternalTable;

const isValidJSON = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};
